import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Calendar, Save, Trash2, Camera } from 'lucide-react';
import { useApp } from '@/store';
import { Billing, ConditionValue, Status } from '@/data/types';
import { addYears } from '@/lib/customer-dates';
import { optionsWith, sortOptions } from '@/lib/hose-options';
import { useT, HOSE_COND_LABEL_KEYS } from '@/lib/i18n';
import {
  conditionOptions,
  CONDITION_LABEL_KEYS,
  faultNotes,
  isConditionFault,
} from '@/lib/hose-condition';
import { useConfirm } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhotoUploader } from '@/components/PhotoUploader';
import { ReadOnlyNotice } from '@/components/ReadOnlyNotice';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { ScanButton } from '@/components/BarcodeScanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { HoseCondition } from '@/data/types';

const conditionKeys: (keyof HoseCondition)[] = [
  'fireCock',
  'seals',
  'hose',
  'nozzle',
  'axle',
  'swivelArm',
  'doorLock',
  'water',
];

// Fallback when the tenant has not configured Hose Options. Hose and
// connection diameters share one inner/outer list (client #89) — i18n-ignore
const DEFAULT_DIAMETERS = ['ø16/24 mm', 'ø19/27 mm', 'ø25/34 mm'];

export function HoseEdit() {
  const t = useT();
  const confirm = useConfirm();
  const { id, hoseId } = useParams();
  const nav = useNavigate();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const workers = useApp(s => s.workers);
  const hoseConfig = useApp(s => s.hoseConfig);
  const manufacturers = useApp(s => s.manufacturers);
  const deviceTypes = useApp(s => s.deviceTypes);
  const toast = useApp(s => s.toast);
  const upsertHose = useApp(s => s.upsertHose);
  const deleteHose = useApp(s => s.deleteHose);
  const existing = customer?.hoses.find(h => h.id === hoseId);
  const rights = useSectionRights();
  const canHose = rights[SectionId.HOSES];

  const [worker, setWorker] = useState(
    existing?.worker ?? workers[0]?.name ?? ''
  );
  const [deviceNumber, setDeviceNumber] = useState(
    existing?.deviceNumber ?? ''
  );
  const [floor, setFloor] = useState(existing?.floor ?? '');
  const [location, setLocation] = useState(existing?.location ?? '');
  const [mfr, setMfr] = useState(existing?.mfr ?? '');
  const [model, setModel] = useState(existing?.model ?? '');
  const [year, setYear] = useState(existing?.year?.toString() ?? '');
  const [length, setLength] = useState(existing?.length?.toString() ?? '');
  // A new hose starts with these unset rather than guessing. They were
  // hardcoded to '25 mm' / '34 mm' / 'Storz C', which invented equipment
  // details: the values are not necessarily in the tenant's configured lists,
  // and every hose opened without a connection pre-selected 'Storz C' and
  // wrote it back on save. That value has since been merged away in the
  // client's own list (client #89), so the form would have kept re-creating a
  // fitting they retired.
  const [hoseDiameter, setHoseDiameter] = useState(existing?.diameter ?? '');
  const [connectionDiameter, setConnectionDiameter] = useState(
    existing?.connectionDiameter ?? ''
  );
  const [connection, setConnection] = useState(existing?.connection ?? '');
  const [condition, setCondition] = useState<HoseCondition>(
    existing?.condition ?? {
      fireCock: ConditionValue.OK,
      seals: ConditionValue.OK,
      hose: ConditionValue.OK,
      nozzle: ConditionValue.OK,
      axle: ConditionValue.OK,
      swivelArm: ConditionValue.OK,
      doorLock: ConditionValue.OK,
      water: ConditionValue.OK,
    }
  );
  const [conditionNotes, setConditionNotes] = useState<
    Partial<Record<keyof HoseCondition, string>>
  >(existing?.conditionNotes ?? {});
  const [conditionReplaced, setConditionReplaced] = useState<
    Partial<Record<keyof HoseCondition, string>>
  >(existing?.conditionReplaced ?? {});

  // Switching a check to New stamps today's replacement date, but only when the
  // field is still empty — re-selecting New must not overwrite a date the
  // technician corrected by hand (client #50).
  const setConditionValue = (k: keyof HoseCondition, v: ConditionValue) => {
    setCondition(c => ({ ...c, [k]: v }));
    if (v === ConditionValue.NEW) {
      setConditionReplaced(r =>
        r[k] ? r : { ...r, [k]: new Date().toISOString().slice(0, 10) }
      );
    }
  };
  const [service, setService] = useState(existing?.lastService ?? '');
  const [next, setNext] = useState(existing?.nextService ?? '');
  const [resting, setResting] = useState(existing?.restingPressure ?? '');
  const [flowP, setFlowP] = useState(existing?.flowPressure ?? '');
  const [flow, setFlow] = useState(existing?.flowRate ?? '');
  const [test, setTest] = useState(existing?.pressureTestDate ?? '');
  const [photos, setPhotos] = useState<string[]>(existing?.photos ?? []);
  const [billing, setBilling] = useState<Billing>(
    existing?.billing ?? Billing.NONE
  );

  // Hose models are configured in the manufacturer catalogue (client 16 July),
  // filtered to hose device types, and only that manufacturer's models show.
  const mfrList = manufacturers ?? [];
  const hoseTypeIds = new Set(
    (deviceTypes ?? [])
      .filter(dt =>
        /loeschposten|löschposten|schlauch|hydrant/i.test(
          `${dt.id} ${dt.nameDe} ${dt.nameEn}`
        )
      )
      .map(dt => dt.id)
  );
  const isHoseModel = (deviceType: string) =>
    hoseTypeIds.size === 0 || hoseTypeIds.has(deviceType);
  const selectedMfr = mfrList.find(m => m.name === mfr);
  const mfrHoseModels = (selectedMfr?.models ?? []).filter(m =>
    isHoseModel(m.deviceType)
  );
  // Client #13: the dropdown listed the whole extinguisher catalogue, so the
  // technician scrolled past dozens of brands that ship no hose at all. Only
  // manufacturers with a hose model configured belong here — plus whatever this
  // hose already names, so an existing record never silently loses its value.
  const hoseMfrList = mfrList.filter(
    m => m.name === mfr || m.models.some(md => isHoseModel(md.deviceType))
  );

  const setToday = (setter: (v: string) => void) =>
    setter(new Date().toISOString().slice(0, 10));
  // One button per service cycle — some customers run 2- or 3-yearly rather
  // than the annual default (client #78).
  const computeNext = (years: number) => {
    if (service) setNext(addYears(service, years));
  };

  if (!customer) return <div>{t('common.notFound')}</div>;
  // Section rights (#95): no new-hose form without Erstellen, and an existing
  // hose opens read-only without Bearbeiten.
  if (!existing && !canHose.create) {
    return <Navigate to={`/customer/${customer.id}/hoses`} replace />;
  }
  const readOnly = !!existing && !canHose.edit;

  const handleSave = async () => {
    const dup =
      !!deviceNumber &&
      [...customer.extinguishers, ...customer.hoses].some(
        d => d.id !== existing?.id && d.deviceNumber === deviceNumber
      );
    if (dup && !(await confirm(t('device.dupWarning')))) return;
    // Keep only notes for checks that still report a fault.
    const notes = faultNotes(condition, conditionNotes);
    // Same shape as notes: keep a replacement date only while that check is
    // still New, so flipping back to OK clears a stale date.
    const replaced = Object.fromEntries(
      conditionKeys
        .filter(
          k => condition[k] === ConditionValue.NEW && conditionReplaced[k]
        )
        .map(k => [k, conditionReplaced[k]])
    );
    upsertHose(customer.id, {
      id: existing?.id ?? `H-${Date.now()}`,
      deviceNumber: deviceNumber || undefined,
      floor: floor || undefined,
      location: location || undefined,
      mfr: mfr || undefined,
      model: model || undefined,
      year: year ? parseInt(year, 10) : undefined,
      length: length ? parseFloat(length) : undefined,
      diameter: hoseDiameter,
      connectionDiameter,
      connection: connection || undefined,
      worker: worker || undefined,
      condition,
      conditionNotes: Object.keys(notes).length ? notes : undefined,
      conditionReplaced: Object.keys(replaced).length ? replaced : undefined,
      lastService: service || undefined,
      nextService: next || undefined,
      restingPressure: resting || undefined,
      flowPressure: flowP || undefined,
      flowRate: flow || undefined,
      pressureTestDate: test || undefined,
      spareParts: existing?.spareParts ?? [],
      billing,
      photos: photos.length ? photos : undefined,
      status: existing?.status ?? Status.NONE,
    });
    toast(t('common.save'));
    nav(`/customer/${customer.id}/hoses`);
  };

  const handleDelete = async () => {
    if (!existing) return;
    if (
      !(await confirm(
        `${t('common.delete')} ${t('hose.fireHose')} ${existing.deviceNumber ?? existing.id}?`
      ))
    )
      return;
    deleteHose(customer.id, existing.id);
    toast(t('common.delete'));
    nav(`/customer/${customer.id}/hoses`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div>
        <Link
          to={`/customer/${customer.id}/hoses`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        {existing && rights[SectionId.MAINTENANCE].read && (
          <Link
            to={`/customer/${customer.id}/device/${existing.id}/history`}
            className="ml-4 text-sm text-primary"
          >
            {t('customer.inspections')}
          </Link>
        )}
        <h1 className="text-2xl font-bold tracking-tight mt-2">
          {existing
            ? readOnly
              ? t('hose.fireHose')
              : `${t('common.edit')} ${t('hose.fireHose')}`
            : t('hose.addHose')}
        </h1>
      </div>

      {readOnly && <ReadOnlyNotice />}

      {/* Disabled fieldsets lock every control of the form at once. */}
      <Card>
        <CardContent className="pt-5">
          <fieldset
            disabled={readOnly}
            className="grid min-w-0 gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
          >
            <div>
              <Label>{t('hose.serviceTechnician')}</Label>
              <Select value={worker} onValueChange={setWorker}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {workers
                    .filter(w => w.active)
                    .map(w => (
                      <SelectItem key={w.id} value={w.name}>
                        {w.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </fieldset>
        </CardContent>
      </Card>

      <fieldset
        disabled={readOnly}
        className="grid min-w-0 gap-4 sm:grid-cols-2 md:grid-cols-3"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('hose.equipment')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <Label>{t('ext.idNumber')}</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="hose-number"
                  value={deviceNumber}
                  onChange={e => setDeviceNumber(e.target.value)}
                />
                <ScanButton onScan={setDeviceNumber} />
              </div>
            </div>
            <div>
              <Label htmlFor="hose-floor">{t('ext.floor')}</Label>
              <Input
                id="hose-floor"
                value={floor}
                onChange={e => setFloor(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('ext.location')}</Label>
              <Input
                data-testid="hose-location"
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('ext.manufacturer')}</Label>
              {hoseMfrList.length > 0 ? (
                <Select
                  value={mfr}
                  onValueChange={v => {
                    setMfr(v);
                    setModel('');
                  }}
                >
                  <SelectTrigger data-testid="hose-mfr">
                    <SelectValue placeholder={t('ext.manufacturer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {[...hoseMfrList]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(m => (
                        <SelectItem key={m.id} value={m.name}>
                          {m.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid="hose-mfr"
                  value={mfr}
                  onChange={e => setMfr(e.target.value)}
                />
              )}
            </div>
            <div>
              <Label>{t('ext.model')}</Label>
              {mfrHoseModels.length > 0 ? (
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('ext.model')} />
                  </SelectTrigger>
                  <SelectContent>
                    {mfrHoseModels.map(m => (
                      <SelectItem key={m.id} value={m.name}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : hoseConfig.hoseModels.length > 0 ? (
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('ext.model')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions(hoseConfig.hoseModels).map(m => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={model} onChange={e => setModel(e.target.value)} />
              )}
            </div>
            <div>
              <Label>{t('table.year')}</Label>
              <Input
                data-testid="hose-year"
                type="number"
                value={year}
                onChange={e => setYear(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('hose.length')}</Label>
              <Input
                data-testid="hose-length"
                type="number"
                value={length}
                onChange={e => setLength(e.target.value)}
              />
            </div>
            <div data-focus="hose-diameter">
              <Label>{t('hose.hoseDiameter')}</Label>
              <Select value={hoseDiameter} onValueChange={setHoseDiameter}>
                <SelectTrigger data-testid="hose-diameter-trigger">
                  <SelectValue placeholder={t('common.notSet')} />
                </SelectTrigger>
                <SelectContent>
                  {optionsWith(
                    hoseConfig.hoseDiameters.length
                      ? hoseConfig.hoseDiameters
                      : DEFAULT_DIAMETERS,
                    hoseDiameter
                  ).map(d => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('hose.connectionDiameter')}</Label>
              <Select
                value={connectionDiameter}
                onValueChange={setConnectionDiameter}
              >
                <SelectTrigger data-testid="connection-diameter-trigger">
                  <SelectValue placeholder={t('common.notSet')} />
                </SelectTrigger>
                <SelectContent>
                  {optionsWith(
                    hoseConfig.connectionDiameters.length
                      ? hoseConfig.connectionDiameters
                      : DEFAULT_DIAMETERS,
                    connectionDiameter
                  ).map(d => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('ext.billing')}</Label>
              {/* Same four classes as the extinguishers so a hose can be
                  charged, and so maintenance mode ticks it on the same rule
                  (client #91). */}
              <Select
                value={billing}
                onValueChange={v => setBilling(v as Billing)}
              >
                <SelectTrigger data-testid="hose-billing">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={Billing.NONE}>
                    {t('ext.billingNone')}
                  </SelectItem>
                  <SelectItem value={Billing.MAINTENANCE}>
                    {t('ext.billingMaintenance')}
                  </SelectItem>
                  <SelectItem value={Billing.NEW}>
                    {t('ext.billingNew')}
                  </SelectItem>
                  <SelectItem value={Billing.DISPOSED}>
                    {t('ext.billingDisposed')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('hose.connection')}</Label>
              {/* Configured list when there is one, free text otherwise, so a
                  tenant that has not filled in Hose Options can still work and
                  an existing value is never silently dropped (client #89). */}
              {(hoseConfig.connections ?? []).length > 0 ? (
                <Select value={connection} onValueChange={setConnection}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('common.notSet')} />
                  </SelectTrigger>
                  <SelectContent>
                    {optionsWith(hoseConfig.connections ?? [], connection).map(
                      cn => (
                        <SelectItem key={cn} value={cn}>
                          {cn}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={connection}
                  onChange={e => setConnection(e.target.value)}
                />
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              {t('hose.conditionChecks')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {conditionKeys.map(k => (
              <div key={k} className="space-y-1">
                <div className="flex flex-col sm:grid sm:grid-cols-2 sm:items-center gap-1 sm:gap-2">
                  <Label className="text-xs">
                    {t(HOSE_COND_LABEL_KEYS[k])}
                  </Label>
                  <Select
                    value={condition[k]}
                    onValueChange={v =>
                      setConditionValue(k, v as ConditionValue)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Options differ per check — the fire cock can drip,
                          the water can be rusty or sandy (client #97). */}
                      {conditionOptions(k, condition[k]).map(v => (
                        <SelectItem key={v} value={v}>
                          {t(CONDITION_LABEL_KEYS[v])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isConditionFault(condition[k]) && (
                  <Input
                    value={conditionNotes[k] ?? ''}
                    onChange={e =>
                      setConditionNotes(n => ({ ...n, [k]: e.target.value }))
                    }
                    placeholder={t('hose.defectNote')}
                    className="text-xs"
                  />
                )}
                {condition[k] === ConditionValue.NEW && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">
                      {t('hose.replacedOn')}
                    </Label>
                    <Input
                      type="date"
                      data-testid={`hose-replaced-${k}`}
                      value={conditionReplaced[k] ?? ''}
                      onChange={e =>
                        setConditionReplaced(r => ({
                          ...r,
                          [k]: e.target.value,
                        }))
                      }
                      className="text-xs"
                    />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('hose.serviceReport')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <Label>{t('hose.serviceDate')}</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="hose-service-date"
                  type="date"
                  value={service}
                  onChange={e => setService(e.target.value)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setToday(setService)}
                >
                  <Calendar className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <Label>{t('hose.nextService')}</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="hose-next-service"
                  type="date"
                  value={next}
                  onChange={e => setNext(e.target.value)}
                />
                {[1, 2, 3].map(y => (
                  <Button
                    key={y}
                    variant="outline"
                    size="sm"
                    data-testid={`hose-next-plus-${y}y`}
                    onClick={() => computeNext(y)}
                  >
                    +{y}y
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t('hose.restingPressure')}</Label>
              <Input
                data-testid="hose-resting-pressure"
                value={resting}
                onChange={e => setResting(e.target.value)}
                placeholder="e.g. 6.0 bar" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('hose.flowPressure')}</Label>
              <Input
                data-testid="hose-flow-pressure"
                value={flowP}
                onChange={e => setFlowP(e.target.value)}
                placeholder="e.g. 4.5 bar" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('hose.flowRate')}</Label>
              <Input
                data-testid="hose-flow-rate"
                value={flow}
                onChange={e => setFlow(e.target.value)}
                placeholder="e.g. 105 L/min" // i18n-ignore
              />
            </div>
            <div>
              <Label>{t('hose.pressureTestDate')}</Label>
              <Input
                data-testid="hose-pressure-test-date"
                type="date"
                value={test}
                onChange={e => setTest(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" />
              {t('photos.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PhotoUploader value={photos} onChange={setPhotos} />
          </CardContent>
        </Card>
      </fieldset>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => nav(`/customer/${customer.id}/hoses`)}
        >
          {readOnly ? t('common.back') : t('common.cancel')}
        </Button>
        {existing && canHose.delete && (
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            {t('common.delete')}
          </Button>
        )}
        {!readOnly && (
          <Button data-testid="hose-save" onClick={handleSave}>
            <Save className="h-4 w-4" />
            {t('common.save')}
          </Button>
        )}
      </div>
    </motion.div>
  );
}
